// assets/es-related-products.js
// (touched 2026-07-25 to force a fresh CDN checksum after a stuck-cache push)
// The "Latest Products" carousel on product pages (snippets/related-product.liquid) renders
// server-side from whatever collection the current product happens to belong to -- reasonable,
// but not aware of the specific car a shopper has picked via the YMM selector. If
// localStorage.esSelectedVehicle is set (assets/vehicle-memory-bar.js writes it, same key
// assets/es-card-enhancements.js already reads for the "Fits your X" card highlight), this
// fetches that exact same tag-scoped collection URL the vehicle pills already use elsewhere in
// the site, pulls real product cards out of it, and swaps them into the carousel in place of
// the generic server-rendered set -- so a shopper who's told the site "I drive a Golf R MK7"
// sees Golf R MK7 parts here too, not just whatever the current product's own collection
// happened to contain. No vehicle selected, or the fetch doesn't turn up enough real matches?
// The original server-rendered set (already genuinely related to the viewed product) stays
// exactly as-is -- this only replaces it when it can offer something more specific.
//
// 2026-07-25: added a second, independent fallback for a real live bug -- when a product has no
// real multi-product collection (snippets/related-product.liquid's own {% else %} branch),
// the server renders snippets/empty-c-product-item-grid-v2.liquid: literal Shopify theme-editor
// demo content ("Example Product", "$328.28", every link pointing at /admin/products) shown to
// real shoppers. That's independent of whether a vehicle is selected -- a shopper with no
// vehicle picked, or one whose vehicle-specific fetch itself comes up short, could still be
// looking at fake placeholder cards. If the carousel's real (non-placeholder, non-current-
// product) item count is below MIN_MATCHES after the vehicle-swap attempt above has already had
// its chance, this backfills with real products: same-vendor products first (most genuinely
// related to what the shopper is already looking at), then site-wide best sellers if the vendor
// alone doesn't have enough. Always real, clickable, purchasable products -- never the demo set.
(function () {
  var STORAGE_KEY = 'esSelectedVehicle';
  var MIN_MATCHES = 3; // below this, a set (vehicle-specific OR the server-rendered default) isn't worth keeping

  function getSelectedVehicle() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed && parsed.url ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  // The base theme's own carousel bootstrap (assets/engo-scripts.js,
  // azirspares_init_carousel) builds its slick config from scratch and only ever reads
  // data-responsive off the element -- it never reads data-slick at all, and never passes an
  // explicit `arrows` option or custom arrow markup either. Confirmed live: slick-initialized
  // was true but there were zero prev/next elements anywhere in the carousel. Rather than
  // patch that shared base file (other carousels on the site use the same function), this
  // un-initializes whatever the base script set up and re-runs slick itself with an explicit
  // config that always includes real arrow markup using the exact .slick-prev/.slick-next
  // classes snippets/related-product.liquid's own <style> block already targets.
  function reinitCarousel(container) {
    if (!window.jQuery || !jQuery.fn.slick) return;
    var $c = jQuery(container);
    if ($c.hasClass('slick-initialized')) {
      $c.slick('unslick');
    }
    var settings = { arrows: true, dots: true, infinite: false, slidesToShow: 4 };
    try {
      var fromAttr = JSON.parse(container.getAttribute('data-slick') || '{}');
      settings = Object.assign(settings, fromAttr);
    } catch (e) {}
    var responsiveAttr = container.getAttribute('data-responsive');
    if (responsiveAttr) {
      try { settings.responsive = JSON.parse(responsiveAttr); } catch (e) {}
    }
    settings.prevArrow = '<button type="button" class="slick-prev" aria-label="Previous"></button>';
    settings.nextArrow = '<button type="button" class="slick-next" aria-label="Next"></button>';
    $c.slick(settings);
    // Confirmed live 2026-07-27: passing the arrow's SVG markup through Slick's prevArrow/
    // nextArrow STRING option (parsed internally via jQuery(htmlString)) produced a button whose
    // outerHTML looked completely correct (svg + polyline present with the right attributes) but whose
    // real getBoundingClientRect() was 0x0 -- a known class of bug where jQuery's string-to-
    // element parser doesn't create true SVG-namespaced nodes from a raw HTML string, so the
    // browser has elements it can serialize but not actually paint. Building the buttons plain
    // via Slick (safe, no SVG involved) and injecting the icon markup afterward with a real
    // innerHTML assignment on the already-live DOM node sidesteps that parser entirely -- setting
    // innerHTML on an element already attached to the document uses the standard HTML parser's
    // foreign-content handling, which creates proper SVG elements every time.
    container.querySelectorAll('.slick-prev, .slick-next').forEach(function (btn) {
      var isPrev = btn.classList.contains('slick-prev');
      btn.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="' + (isPrev ? '15 18 9 12 15 6' : '9 18 15 12 9 6') + '"></polyline></svg>';
    });
  }

  // Shared by the vehicle-specific fetch and the vendor/best-seller backfill fetches below --
  // parses a fetched collection page's #es-grid (present on every collection-template.liquid
  // page, confirmed live on /collections/vendors?q=X and /collections/all) and returns real
  // product-card items, excluding the current product and anything already in excludeHandles.
  //
  // DIRECT children only, same reasoning as assets/vehicle-auto-filter.js -- the theme's own
  // card markup nests an inner element that ALSO carries the "product-item" class inside the
  // real outer `col-md-3 col-sm-6 col-xs-12 product-item` grid-column wrapper, so a plain
  // descendant querySelectorAll grabs a mix of outer wrappers and inner unwrapped elements
  // depending on the item -- exactly what produced the original "slim card" bug.
  function extractGridItems(html, currentHandle, excludeHandles) {
    if (!html) return [];
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var grid = doc.getElementById('es-grid');
    if (!grid) return [];
    return Array.from(grid.children).filter(function (item) {
      if (!item.classList || !item.classList.contains('product-item')) return false;
      var link = item.querySelector('a[href*="/products/"]');
      if (!link) return false;
      var href = link.getAttribute('href') || '';
      if (currentHandle && href.indexOf('/products/' + currentHandle) !== -1) return false;
      if (excludeHandles && excludeHandles.some(function (h) { return href.indexOf('/products/' + h) !== -1; })) return false;
      return true;
    });
  }

  // Counts the REAL (non-placeholder, non-current-product) cards already in the carousel, as
  // rendered server-side. A placeholder card (snippets/empty-c-product-item-grid-v2.liquid)
  // always links to /admin/products -- that's the one reliable marker distinguishing it from a
  // genuine product-item card, which always links to a real /products/<handle> URL.
  function countRealItems(carousel, currentHandle) {
    var items = carousel.querySelectorAll('.product-item');
    var count = 0;
    items.forEach(function (item) {
      var link = item.querySelector('a[href*="/products/"]');
      if (!link) return; // placeholder cards link to /admin/products, never /products/
      var href = link.getAttribute('href') || '';
      if (currentHandle && href.indexOf('/products/' + currentHandle) !== -1) return;
      count++;
    });
    return count;
  }

  // Reuses the item itself as the slide instead of wrapping it in a NEW col-xs-12 div -- it
  // already carries real Bootstrap column classes (col-md-3 col-sm-6 col-xs-12) from its origin
  // as a grid card; wrapping it again nests a grid column inside a grid column. Swapping its own
  // class to a clean col-xs-12 makes it match the server-rendered slides' structure exactly.
  function itemsToMarkup(items, slideCount) {
    return items.slice(0, slideCount).map(function (item) {
      var clone = item.cloneNode(true);
      clone.className = 'col-xs-12';
      return clone.outerHTML;
    }).join('');
  }

  // Must unslick BEFORE replacing innerHTML, not after -- slick moves the original slides into
  // its own .slick-list/.slick-track wrapper and remembers that structure internally.
  // Overwriting innerHTML directly while slick still thinks it owns that DOM (the carousel
  // element keeps its slick-initialized class across the swap) leaves slick's internal state
  // pointing at markup that no longer exists, corrupting the next re-init. Unslicking on a
  // clean, still-slick-owned DOM first, then swapping innerHTML, then re-initializing fresh
  // avoids that.
  function swapCarouselContent(carousel, markup) {
    if (window.jQuery && jQuery(carousel).hasClass('slick-initialized')) {
      jQuery(carousel).slick('unslick');
    }
    carousel.innerHTML = markup;
    reinitCarousel(carousel);
    // Fresh cards need the same per-card JS pass (fits-matching, rating resolution) that runs
    // on the collection/search grids -- es-card-enhancements.js exposes nothing globally to
    // re-trigger, but it also self-polls on an interval already covering the first ~7.5s after
    // page load, which comfortably covers this fetch+swap. No action needed here beyond the swap.
  }

  function backfillWithRealProducts(carousel, currentHandle, slideCount, existingHandles) {
    var vendor = (window.ES_PRODUCT_DATA && window.ES_PRODUCT_DATA.vendor) || '';
    var vendorUrl = vendor ? '/collections/vendors?q=' + encodeURIComponent(vendor) : null;
    var bestSellersUrl = '/collections/all?sort_by=best-selling';

    function fetchGrid(url) {
      if (!url) return Promise.resolve([]);
      return fetch(url, { cache: 'no-store' })
        .then(function (res) { return res.ok ? res.text() : null; })
        .then(function (html) { return extractGridItems(html, currentHandle, existingHandles); })
        .catch(function () { return []; });
    }

    fetchGrid(vendorUrl)
      .then(function (vendorItems) {
        if (vendorItems.length >= slideCount) return vendorItems;
        // Vendor alone didn't fill the carousel -- top up with best sellers, skipping anything
        // the vendor fetch already picked so the same product never shows twice.
        var vendorHandles = vendorItems.map(function (item) {
          var link = item.querySelector('a[href*="/products/"]');
          var m = link && link.getAttribute('href').match(/\/products\/([^/?#]+)/);
          return m ? m[1] : null;
        }).filter(Boolean);
        return fetchGrid(bestSellersUrl).then(function (bestSellerItems) {
          var filtered = bestSellerItems.filter(function (item) {
            var link = item.querySelector('a[href*="/products/"]');
            var href = link ? link.getAttribute('href') : '';
            return !vendorHandles.some(function (h) { return href.indexOf('/products/' + h) !== -1; });
          });
          return vendorItems.concat(filtered);
        });
      })
      .then(function (combined) {
        if (combined.length < MIN_MATCHES) return; // still nothing real worth showing -- leave as-is rather than an even-thinner swap
        swapCarouselContent(carousel, itemsToMarkup(combined, slideCount));
      })
      .catch(function () {});
  }

  function init() {
    var wrap = document.querySelector('.related-product');
    var carousel = document.getElementById('es-related-carousel');
    if (!wrap || !carousel) return;

    // Always re-init with real arrows, regardless of what happens below -- this is what
    // actually makes the arrows show up at all, independent of any content swap.
    reinitCarousel(carousel);

    var currentHandle = wrap.getAttribute('data-current-product-handle') || '';
    var slideCount = carousel.querySelectorAll('.col-xs-12').length || 8;
    var vehicle = getSelectedVehicle();

    function maybeBackfillDefault() {
      if (countRealItems(carousel, currentHandle) >= MIN_MATCHES) return; // server-rendered set is already fine
      backfillWithRealProducts(carousel, currentHandle, slideCount, []);
    }

    if (!vehicle) { maybeBackfillDefault(); return; }

    fetch(vehicle.url, { cache: 'no-store' })
      .then(function (res) { return res.ok ? res.text() : null; })
      .then(function (html) {
        var items = extractGridItems(html, currentHandle, []);
        if (items.length < MIN_MATCHES) { maybeBackfillDefault(); return; } // vehicle fetch itself came up short
        swapCarouselContent(carousel, itemsToMarkup(items, slideCount));
      })
      .catch(function () { maybeBackfillDefault(); });
  }

  document.addEventListener('DOMContentLoaded', init);
})();
